import { FC, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Spinner, Stack, Tooltip, Typography, NativeSelect } from "@altimateai/lego";
import { IconInfoCircle } from "@tabler/icons-react";
import { Chart, ChartTypes } from "@uicore";
import { Bar } from "recharts";
import dayjs from "dayjs";
import { formatCost } from "@/helpers/utils";
import { useAppState } from "@/modules/app/useAppContext";
import { getAIServiceBreakdown } from "@/helpers/apis";
import { AGGREGATE_OPTIONS } from "./constants";
import { createRoundedBarShape, createTooltipContent } from "./chartUtils";
import type { AIServiceGraphResponse } from "./types";

const BREAKDOWN_COLORS = [
  "#247EFE",
  "#EE46BC",
  "#01CD8C",
  "#9382D9",
  "#FAC515",
  "#FF9F43",
  "#FF6B6B",
  "#5B9FFF",
  "#A66DFF",
  "#36BFFA",
];

const OTHER_COLOR = "#94A3B8";

const NONE_OPTION = { label: "None", value: "__none__" };

interface Props {
  graphData?: AIServiceGraphResponse;
  isLoading: boolean;
  aggregateBy: string;
  onAggregateChange: (value: string) => void;
  serviceColor: string;
  serviceType: string;
  dateParams: { start_date: string; end_date: string };
  instanceId?: string;
}

export const AIServiceDetailCostGraph: FC<Props> = ({
  graphData,
  isLoading,
  aggregateBy,
  onAggregateChange,
  serviceColor,
  serviceType,
  dateParams,
  instanceId,
}) => {
  const { currency } = useAppState();
  const [groupBy, setGroupBy] = useState<string>("__none__");

  const isGrouped = groupBy !== "__none__";

  const { data: breakdownData, isLoading: isBreakdownLoading } = useQuery({
    queryKey: ["ai_service_breakdown", serviceType, dateParams, aggregateBy, groupBy, instanceId],
    queryFn: () =>
      getAIServiceBreakdown(serviceType, {
        ...dateParams,
        ...(instanceId ? { instance_id: instanceId } : {}),
        aggregation_level: aggregateBy,
        dimension: groupBy,
      }),
    enabled: Boolean(serviceType),
  });

  // Build group-by options from breakdown response
  const groupByOptions = useMemo(() => {
    if (!breakdownData?.available_dimensions) return [NONE_OPTION];
    return [
      NONE_OPTION,
      ...breakdownData.available_dimensions.map(d => ({
        label: d.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
        value: d,
      })),
    ];
  }, [breakdownData?.available_dimensions]);

  const pivotedData = breakdownData?.pivoted_data ?? [];
  const dimensionKeys = breakdownData?.dimension_keys ?? [];

  const showBreakdown = isGrouped && pivotedData.length > 0;
  const hasOtherBucket = dimensionKeys.includes("Other");
  const effectiveData = showBreakdown ? pivotedData : graphData?.graph;
  const effectiveLoading = isLoading || (isGrouped && isBreakdownLoading);

  return (
    <Stack direction="column" className="al-p-3 al-pb-10 al-border al-rounded-md al-mt-3">
      <Stack align="center" justify="between" className="al-w-full al-mb-2">
        <Stack align="center" gap="xs">
          <Typography variant="section-heading">Costs</Typography>
          {showBreakdown && hasOtherBucket && (
            <Tooltip title="Showing top 5 values by cost. Remaining values are grouped as 'Other'.">
              <span className="al-text-light-gray al-cursor-help" aria-label="Breakdown info">
                <IconInfoCircle size={16} />
              </span>
            </Tooltip>
          )}
        </Stack>
        <Stack align="center" gap="md">
          {groupByOptions.length > 1 && (
            <NativeSelect
              size="sm"
              variant="ghost"
              value={groupBy}
              onChange={setGroupBy}
              options={groupByOptions}
              label="Group by:"
              showPlaceholder
              buttonProps={{ "aria-label": "Group by dimension" }}
            />
          )}
          <NativeSelect
            size="sm"
            variant="ghost"
            value={aggregateBy}
            onChange={onAggregateChange}
            options={AGGREGATE_OPTIONS}
            label="Aggregate by:"
            showPlaceholder
            buttonProps={{ "aria-label": "Aggregation level" }}
          />
        </Stack>
      </Stack>

      {effectiveLoading ? (
        <Stack align="center" justify="center" gap="sm" className="al-w-full al-min-h-[300px]">
          <Spinner size="md" />
          Loading graph...
        </Stack>
      ) : effectiveData && effectiveData.length > 0 ? (
        <Chart
          title=""
          xAxisDataKey="date"
          yAxisLabel={{ value: "Cost" }}
          xAxisLabelFormatter={(value: string) => dayjs.utc(value).format("DD MMM YY")}
          yAxisLabelFormatter={(value: string) => formatCost(value, currency)}
          type={ChartTypes.BarChart}
          data={effectiveData}
          hideFullscreenButton
          tooltipProps={{
            content: createTooltipContent(aggregateBy),
          }}
          chartProps={{}}
          legendProps={{ wrapperStyle: { bottom: -35 } }}
        >
          {({ barSize, selectedLegend }: { barSize: number; selectedLegend?: string }) =>
            showBreakdown ? (
              <>
                {dimensionKeys.map((key, i) => (
                  <Bar
                    key={key}
                    barSize={Math.min(barSize, 60)}
                    maxBarSize={60}
                    dataKey={key}
                    name={key}
                    fill={
                      key === "Other" ? OTHER_COLOR : BREAKDOWN_COLORS[i % BREAKDOWN_COLORS.length]
                    }
                    stackId="breakdown"
                    hide={Boolean(selectedLegend && selectedLegend !== key)}
                    shape={createRoundedBarShape(i === dimensionKeys.length - 1)}
                  />
                ))}
              </>
            ) : (
              <Bar
                barSize={Math.min(barSize, 60)}
                maxBarSize={60}
                dataKey="cost"
                name="Cost"
                fill={serviceColor}
                hide={Boolean(selectedLegend && selectedLegend !== "cost")}
                shape={createRoundedBarShape(true)}
              />
            )
          }
        </Chart>
      ) : (
        <Stack align="center" justify="center" className="al-w-full al-min-h-[200px]">
          <div className="al-text-light-gray al-text-center al-p-4">No data is present</div>
        </Stack>
      )}
    </Stack>
  );
};
